import React, { useEffect, useState, useCallback } from 'react';
import './ChartPatternScanner.css';
import {
  fetchPatternTypes,
  fetchPatternResults,
  fetchPatternSectors,
  ChartPatternType,
  ChartPatternResult,
} from '../../services/chartPatternApi.ts';

const PAGE_SIZE = 20;
const TIMEFRAMES = ['DAILY'];
const MCAP_CATEGORIES = ['SMALLCAP', 'MIDCAP', 'LARGECAP'];

const FilterIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <line x1="2" y1="4" x2="14" y2="4"/><line x1="4" y1="8" x2="12" y2="8"/><line x1="6" y1="12" x2="10" y2="12"/>
  </svg>
);

const ChartPatternScanner: React.FC = () => {
  const [types, setTypes]     = useState<ChartPatternType[]>([]);
  const [sectors, setSectors] = useState<string[]>([]);
  const [allResults, setAllResults] = useState<ChartPatternResult[] | null>(null);
  const [loading, setLoading]     = useState(false);
  const [page, setPage]           = useState(0);
  const [copiedId, setCopiedId]   = useState<number | null>(null);
  const [sortCol, setSortCol]     = useState<'changePct' | 'marketCap' | null>('changePct');
  const [sortDir, setSortDir]     = useState<'asc' | 'desc'>('desc');

  const toggleSort = (col: 'changePct' | 'marketCap') => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
    setPage(0);
  };

  const [selectedPattern, setSelectedPattern]   = useState('');
  const [selectedTimeframe, setSelectedTimeframe] = useState('DAILY');
  const [selectedMcap, setSelectedMcap]           = useState('');
  const [mcapMin, setMcapMin]                     = useState('');
  const [mcapMax, setMcapMax]                     = useState('');
  const [selectedSector, setSelectedSector]       = useState('');

  useEffect(() => {
    fetchPatternTypes().then(setTypes).catch(console.error);
    fetchPatternSectors().then(setSectors).catch(console.error);
  }, []);

  const search = useCallback(async () => {
    setLoading(true);
    setPage(0);
    setSortCol('changePct');
    setSortDir('desc');
    try {
      const data = await fetchPatternResults({
        patternCode:   selectedPattern   || undefined,
        timeframe:     selectedTimeframe || undefined,
        marketCapName: selectedMcap      || undefined,
        marketCapMin:  mcapMin ? Number(mcapMin) : undefined,
        marketCapMax:  mcapMax ? Number(mcapMax) : undefined,
        sector:        selectedSector    || undefined,
      });
      setAllResults(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [selectedPattern, selectedTimeframe, selectedMcap, mcapMin, mcapMax, selectedSector]);

  const clearFilters = () => {
    setSelectedPattern('');
    setSelectedMcap('');
    setMcapMin('');
    setMcapMax('');
    setSelectedSector('');
  };

  const reversal     = types.filter(t => t.category === 'REVERSAL');
  const continuation = types.filter(t => t.category === 'CONTINUATION');
  const selectedPatternType = types.find(t => t.code === selectedPattern);

  const activeTags: { label: string; cls: string; clear: () => void }[] = [];
  if (selectedPattern && selectedPatternType)
    activeTags.push({ label: selectedPatternType.name, cls: selectedPatternType.category === 'REVERSAL' ? 'reversal-tag' : 'continuation-tag', clear: () => setSelectedPattern('') });
  if (selectedTimeframe)
    activeTags.push({ label: selectedTimeframe, cls: '', clear: () => setSelectedTimeframe('') });
  if (selectedMcap)
    activeTags.push({ label: selectedMcap, cls: '', clear: () => setSelectedMcap('') });
  if (mcapMin || mcapMax)
    activeTags.push({ label: `₹${mcapMin || 0}–${mcapMax || '∞'} Cr`, cls: '', clear: () => { setMcapMin(''); setMcapMax(''); } });
  if (selectedSector)
    activeTags.push({ label: selectedSector, cls: '', clear: () => setSelectedSector('') });

  const fmt = (n: number | null) =>
    n == null ? '—' : n.toLocaleString('en-IN', { maximumFractionDigits: 2 });

  // Client-side sort + pagination
  const safeResults = Array.isArray(allResults) ? allResults : [];
  const sortedResults = sortCol
    ? [...safeResults].sort((a, b) => {
        const av = a[sortCol] ?? -Infinity;
        const bv = b[sortCol] ?? -Infinity;
        return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
      })
    : safeResults;
  const totalItems  = sortedResults.length;
  const totalPages  = Math.ceil(totalItems / PAGE_SIZE);
  const pageData    = sortedResults.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const from        = totalItems === 0 ? 0 : page * PAGE_SIZE + 1;
  const to          = Math.min((page + 1) * PAGE_SIZE, totalItems);
  const asOfDate    = safeResults.length > 0 ? safeResults[0].asOfDate : null;

  const SortIcon = ({ col }: { col: 'changePct' | 'marketCap' }) => {
    const active = sortCol === col;
    const asc    = active && sortDir === 'asc';
    const desc   = active && sortDir === 'desc';
    return (
      <svg className={`cps-sort-icon${active ? ' active' : ''}`}
        width="10" height="12" viewBox="0 0 10 12" fill="none" aria-hidden="true">
        {/* up chevron */}
        <path d="M5 1 L9 5 L1 5 Z"
          fill={asc ? 'var(--cps-accent)' : 'currentColor'}
          opacity={asc ? 1 : 0.3} />
        {/* down chevron */}
        <path d="M5 11 L9 7 L1 7 Z"
          fill={desc ? 'var(--cps-accent)' : 'currentColor'}
          opacity={desc ? 1 : 0.3} />
      </svg>
    );
  };

  const buildPages = () => {
    const delta = 2;
    const pages: (number | '...')[] = [];
    for (let i = 0; i < totalPages; i++) {
      if (i === 0 || i === totalPages - 1 || (i >= page - delta && i <= page + delta)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    return pages;
  };

  return (
    <div className="cps-page">
      <div className="cps-header">
        <div className="cps-header-left">
          <h1>Pattern Scanner</h1>
        </div>
      </div>

      {/* Filter card */}
      <div className="cps-filters">
        <div className="cps-filter-topbar">
          <span className="cps-filter-topbar-title"><FilterIcon /> Filters</span>
          {activeTags.length > 0 && (
            <div className="cps-active-tags">
              {activeTags.map((tag, i) => (
                <span key={i} className={`cps-active-tag ${tag.cls}`}>
                  {tag.label}
                  <button onClick={tag.clear} aria-label="Remove filter">✕</button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="cps-filter-body">
          <div className="cps-pattern-section">
            <div className="cps-pattern-col">
              <div className="cps-pattern-col-header">
                <span className="cps-pattern-col-dot reversal" />
                <span className="cps-pattern-col-label">Reversal</span>
              </div>
              <div className="cps-chips">
                {reversal.map(t => (
                  <span key={t.code} className={`cps-chip reversal${selectedPattern === t.code ? ' active' : ''}`}
                    onClick={() => setSelectedPattern(selectedPattern === t.code ? '' : t.code)}>
                    {t.name}
                  </span>
                ))}
              </div>
            </div>
            <div className="cps-pattern-col">
              <div className="cps-pattern-col-header">
                <span className="cps-pattern-col-dot continuation" />
                <span className="cps-pattern-col-label">Continuation</span>
              </div>
              <div className="cps-chips">
                {continuation.map(t => (
                  <span key={t.code} className={`cps-chip continuation${selectedPattern === t.code ? ' active' : ''}`}
                    onClick={() => setSelectedPattern(selectedPattern === t.code ? '' : t.code)}>
                    {t.name}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="cps-filter-row">
            <div className="cps-filter-cell">
              <span className="cps-filter-cell-label">Timeframe</span>
              <div className="cps-chips">
                {TIMEFRAMES.map(tf => (
                  <span key={tf} className="cps-chip neutral active" style={{ cursor: 'default' }}>
                    {tf}
                  </span>
                ))}
              </div>
            </div>

            <div className="cps-filter-cell">
              <span className="cps-filter-cell-label">Market Cap</span>
              <div className="cps-chips">
                {MCAP_CATEGORIES.map(mc => (
                  <span key={mc} className={`cps-chip neutral${selectedMcap === mc ? ' active' : ''}`}
                    onClick={() => setSelectedMcap(selectedMcap === mc ? '' : mc)}>
                    {mc}
                  </span>
                ))}
              </div>
              <div className="cps-mcap-range">
                <input type="number" placeholder="Min Cr" value={mcapMin} onChange={e => setMcapMin(e.target.value)} />
                <span>–</span>
                <input type="number" placeholder="Max Cr" value={mcapMax} onChange={e => setMcapMax(e.target.value)} />
                <span style={{ fontSize: '0.7rem', color: 'var(--cps-text-subtle)' }}>Crores</span>
              </div>
            </div>

            <div className="cps-filter-cell">
              <span className="cps-filter-cell-label">Sector</span>
              <select className="cps-select" value={selectedSector} onChange={e => setSelectedSector(e.target.value)}>
                <option value="">All Sectors</option>
                {sectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="cps-filter-actions">
              <button className="cps-btn-apply" onClick={search}>Scan</button>
              <button className="cps-btn-clear" onClick={clearFilters}>Clear</button>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
{loading ? (
        <div className="cps-loading">Scanning...</div>
      ) : allResults !== null ? (
        <>
          <div className="cps-results-bar">
            <h3>Results</h3>
            <span className="cps-results-count">{totalItems} stocks</span>
          </div>
          {asOfDate && (
            <p className="cps-as-of">Stock results captured as of <strong>{asOfDate}</strong></p>
          )}

          {totalItems === 0 ? (
            <div className="cps-empty">
              <div className="cps-empty-icon">📊</div>
              No stocks match your filters.
            </div>
          ) : (
            <div className="cps-table-outer">
            <div className="cps-table-wrap">
              <table className="cps-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Symbol</th>
                    <th>Company</th>
                    <th>Pattern</th>
                    <th>Timeframe</th>
                    <th>Close</th>
                    <th className="cps-th-sortable" onClick={() => toggleSort('changePct')}>
                      Chg% <SortIcon col="changePct" />
                    </th>
                    <th className="cps-th-sortable" onClick={() => toggleSort('marketCap')}>
                      Mkt Cap (Cr) <SortIcon col="marketCap" />
                    </th>
                    <th className="cps-col-captype">Cap Type</th>
                    <th className="cps-col-sector">Sector</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map((r: ChartPatternResult, idx: number) => (
                    <tr key={r.id}>
                      <td><span className="cps-sr">{page * PAGE_SIZE + idx + 1}</span></td>
                      <td>
                        <span className="cps-symbol-cell">
                          <span className="cps-symbol">{r.symbol}</span>
                          <button
                            className={`cps-copy-btn${copiedId === r.id ? ' copied' : ''}`}
                            title="Copy symbol"
                            onClick={() => {
                              navigator.clipboard.writeText(r.symbol);
                              setCopiedId(r.id);
                              setTimeout(() => setCopiedId(null), 1500);
                            }}
                          >
                            {copiedId === r.id ? (
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="2 8 6 12 14 4"/></svg>
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="5" width="9" height="9" rx="1.5"/><path d="M4 11H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v1"/></svg>
                            )}
                          </button>
                        </span>
                      </td>
                      <td><span className="cps-company">{r.companyName || '—'}</span></td>
                      <td><span className="cps-pattern-name">{r.patternName || '—'}</span></td>
                      <td>
                        <span className="cps-tf-badge">
                          {r.timeframe === 'DAILY' ? 'D' : r.timeframe === 'WEEKLY' ? 'W' : r.timeframe === 'MONTHLY' ? 'M' : r.timeframe}
                        </span>
                      </td>
                      <td><span className="cps-price">₹{fmt(r.closePrice)}</span></td>
                      <td>
                        {r.changePct != null
                          ? <span className={r.changePct >= 0 ? 'cps-positive' : 'cps-negative'}>
                              {(r.changePct >= 0 ? '+' : '') + fmt(r.changePct) + '%'}
                            </span>
                          : '—'}
                      </td>
                      <td><span className="cps-price">{r.marketCap ? fmt(r.marketCap) : '—'}</span></td>
                      <td className="cps-col-captype">{r.marketCapName ? <span className="cps-mcap-pill">{r.marketCapName}</span> : '—'}</td>
                      <td className="cps-col-sector"><span className="cps-sector">{r.sector || '—'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          )}

          {totalPages > 1 && (
            <div className="cps-pagination">
              <span className="cps-pg-info">Showing {from}–{to} of {totalItems}</span>
              <button className="cps-pg-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
              {buildPages().map((p, i) =>
                p === '...'
                  ? <span key={`e${i}`} className="cps-pg-ellipsis">…</span>
                  : <button key={p} className={`cps-pg-btn${page === p ? ' active' : ''}`}
                      onClick={() => setPage(p as number)}>
                      {(p as number) + 1}
                    </button>
              )}
              <button className="cps-pg-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </>
      ) : (
        <div className="cps-empty">
          <div className="cps-empty-icon">📐</div>
          Select a pattern and click <strong>Scan</strong> to find stocks.
        </div>
      )}
    </div>
  );
};

export default ChartPatternScanner;
